import { can, type PermissionMatrix } from './RbacPolicy';
import type { Role } from '../vocabulary';

/**
 * Qué rol se le puede dar a quién, y por qué no.
 *
 * Dos reglas, y ninguna se deduce del nombre del rol:
 *
 *  - **Escalar.** Sólo un superusuario nombra superusuarios. Sin esto, cualquier
 *    administrador de área se asciende a sí mismo a todas las áreas.
 *  - **Dejar gente sin firma.** Aprobar lo concede el permiso `aprobar`, que en la
 *    matriz sembrada tienen `lider` y `super` y nadie más. A quién le firma cada
 *    quien lo dicen las filas de `personLeaders`, que **no miran el rol**. Nada
 *    ataba las dos cosas, y el estado que eso permite ya está en producción: una
 *    persona firmada por un `admin` cuyas aprobaciones el servidor rechaza por
 *    falta de permiso, sin decírselo a nadie.
 *
 * La segunda la vuelve a comprobar el servidor, que es quien manda. Ésta existe
 * para que el motivo se lea **antes** de pulsar: un rol deshabilitado que explica
 * por qué informa más que un error después del hecho.
 *
 * Devuelve la razón, no un booleano, porque la razón es lo que se enseña. Corta a
 * propósito: se lee bajo un aviso que ya dice a cuánta gente le firma y dónde
 * reasignarla, y repetir la cifra en cada rol bloqueado la convierte en ruido.
 */
/**
 * Los roles sin frontera de área. Espeja `CROSS_AREA_ROLES` de
 * `functions/src/shared.ts`, que este paquete no puede importar.
 */
const CROSS_AREA: readonly Role[] = ['global', 'super'];

export const whyRoleUnavailable = (input: {
  readonly role: Role;
  /** El rol que la persona tiene hoy. */
  readonly currentRole: Role;
  /** El rol de quien está haciendo el cambio. `null` mientras no haya sesión. */
  readonly callerRole: Role | null;
  /** A cuánta gente le firma la persona hoy. */
  readonly reportCount: number;
  readonly matrix: PermissionMatrix;
}): string | null => {
  /*
   * Los roles de toda la empresa los da y los quita sólo un superusuario — la
   * misma regla que `crossAreaRoleBlocker` en el servidor, que es quien manda.
   *
   * La mitad de dar sólo protegía `super` y el servidor protegía también
   * `global`: la pantalla ofrecía un rol que la llamada rechazaba. La mitad de
   * quitar no existía, y costó tres veces el 28 sep 2026 —un administrador global
   * perdió el rol desde su propia cuenta queriendo ser además líder—.
   */
  if (input.callerRole !== 'super') {
    if (CROSS_AREA.includes(input.role) && input.role !== input.currentRole) {
      return 'Sólo un superusuario puede otorgar este rol.';
    }
    if (CROSS_AREA.includes(input.currentRole) && input.role !== input.currentRole) {
      return 'Sólo un superusuario puede cambiarle el rol a un administrador global.';
    }
  }

  if (input.reportCount > 0 && !can(input.matrix, input.role, 'aprobar')) {
    return 'Este rol no aprueba solicitudes.';
  }

  return null;
};
